// NewKBDateFilter.js
import React from 'react';
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    PopoverCloseButton,
    PopoverHeader,
    PopoverBody,
    Button,
    ButtonGroup,
    VStack,
    HStack,
    Select,
    Box,
    useToast
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { DateRangePicker } from 'react-dates';
import moment from 'moment';

const NewKBDateFilter = ({
    appliedDateFilter,
    setAppliedDateFilter,
    dateFilter,
    setDateFilter,
    datePreset,
    setDatePreset,
    focusedInput,
    setFocusedInput
}) => {
    const toast = useToast();

    const handleDatePresetChange = (preset) => {
        setDatePreset(preset);
        
        let startDate, endDate;
        
        switch(preset) {
            case 'today':
                startDate = moment().startOf('day');
                endDate = moment().endOf('day');
                break;
            case 'yesterday':
                startDate = moment().subtract(1, 'days').startOf('day');
                endDate = moment().subtract(1, 'days').endOf('day');
                break;
            case 'thisWeek':
                startDate = moment().startOf('week');
                endDate = moment().endOf('week');
                break;
            case 'lastWeek':
                startDate = moment().subtract(1, 'week').startOf('week');
                endDate = moment().subtract(1, 'week').endOf('week');
                break;
            case 'thisMonth':
                startDate = moment().startOf('month');
                endDate = moment().endOf('month');
                break;
            case 'lastMonth':
                startDate = moment().subtract(1, 'month').startOf('month');
                endDate = moment().subtract(1, 'month').endOf('month');
                break;
            case 'last30Days':
                startDate = moment().subtract(30, 'days').startOf('day');
                endDate = moment().endOf('day');
                break;
            case 'last60Days':
                startDate = moment().subtract(60, 'days').startOf('day');
                endDate = moment().endOf('day');
                break;
            case 'last90Days':
                startDate = moment().subtract(90, 'days').startOf('day');
                endDate = moment().endOf('day');
                break;
            case 'thisYear':
                startDate = moment().startOf('year');
                endDate = moment().endOf('year');
                break;
            case 'lastYear':
                startDate = moment().subtract(1, 'year').startOf('year');
                endDate = moment().subtract(1, 'year').endOf('year');
                break;
            default:
                return;
        }
        
        setDateFilter({
            startDate,
            endDate,
            focusedInput: null
        });
    };

    const handleApplyDateFilter = () => {
        if (dateFilter.startDate && dateFilter.endDate) {
            setAppliedDateFilter({
                startDate: dateFilter.startDate,
                endDate: dateFilter.endDate
            });
            
            // Update URL params
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.set('startDate', dateFilter.startDate.format('YYYY-MM-DD'));
            urlParams.set('endDate', dateFilter.endDate.format('YYYY-MM-DD'));
            
            // Update URL without refreshing the page
            window.history.replaceState(
                {},
                '',
                `${window.location.pathname}?${urlParams}`
            );

            console.log('[NewKBDateFilter] Applied date filter:', {
                startDate: dateFilter.startDate.format('YYYY-MM-DD'),
                endDate: dateFilter.endDate.format('YYYY-MM-DD')
            });
        } else {
            toast({
                title: "Invalid date range",
                description: "Please select both start and end dates",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleClearDateFilter = () => {
        setDateFilter({
            startDate: null,
            endDate: null,
            focusedInput: null
        });
        setAppliedDateFilter({
            startDate: null,
            endDate: null
        });
        setDatePreset('');
        
        // Remove URL params
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete('startDate');
        urlParams.delete('endDate');
        
        // Update URL without refreshing the page
        window.history.replaceState(
            {},
            '',
            `${window.location.pathname}?${urlParams}`
        );
    };

    return (
        <Popover placement="bottom-start" closeOnBlur={true}>
            <PopoverTrigger>
                <Button 
                  size="sm" 
                  variant="outline"
                  rightIcon={<ChevronDownIcon boxSize={3} />} 
                  colorScheme={appliedDateFilter.startDate ? "orange" : "gray"}
                >
                {appliedDateFilter.startDate && appliedDateFilter.endDate ?
                    `${appliedDateFilter.startDate.format('M/D')} - ${appliedDateFilter.endDate.format('M/D')}` :
                    "Date"}
                </Button>
            </PopoverTrigger>
            <PopoverContent width="auto" p={2}>
                <PopoverArrow />
                <PopoverCloseButton size="sm" />
                <PopoverHeader fontWeight="medium" pb={1} fontSize="sm">Filter by Date</PopoverHeader>
                <PopoverBody p={0}>
                    <VStack align="stretch" spacing={1} p={2}>
                        <HStack spacing={2}>
                            <Select 
                                placeholder="Select preset..." 
                                size="sm"
                                value={datePreset}
                                onChange={(e) => handleDatePresetChange(e.target.value)}
                            >
                                <option value="today">Today</option>
                                <option value="yesterday">Yesterday</option>
                                <option value="thisWeek">This Week</option>
                                <option value="lastWeek">Last Week</option>
                                <option value="thisMonth">This Month</option>
                                <option value="lastMonth">Last Month</option>
                                <option value="last30Days">Last 30 Days</option>
                                <option value="last60Days">Last 60 Days</option>
                                <option value="last90Days">Last 90 Days</option>
                                <option value="thisYear">This Year</option>
                                <option value="lastYear">Last Year</option>
                            </Select>
                        </HStack>
                        
                        <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={2}>
                            <DateRangePicker
                                startDate={dateFilter.startDate}
                                startDateId="date-filter-start-date"
                                endDate={dateFilter.endDate}
                                endDateId="date-filter-end-date"
                                onDatesChange={({ startDate, endDate }) => 
                                    setDateFilter({ ...dateFilter, startDate, endDate })
                                }
                                focusedInput={focusedInput}
                                onFocusChange={(focusedInput) => 
                                    setFocusedInput(focusedInput)
                                }
                                hideKeyboardShortcutsPanel
                                small
                            />
                        </Box>
                        
                        <ButtonGroup size="sm" spacing={2} pt={2}>
                            <Button onClick={handleClearDateFilter} size="sm" variant="outline">
                                Clear
                            </Button>
                            <Button onClick={handleApplyDateFilter} size="sm" colorScheme="blue">
                                Apply
                            </Button>
                        </ButtonGroup>
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

export default NewKBDateFilter;
